import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, MapPin, Users, Key, Activity, LogOut, Building2, Database, Package, Calculator, FileText, Truck, Bell, ScanLine, LifeBuoy, Bug, Tag, Wallet, Gift, ShieldCheck, ScrollText, Landmark, Briefcase, Bot, Download, Store, Handshake, Menu, X } from 'lucide-react';
import { logout, type AdminSession } from '../auth/auth';
import { ToastProvider } from './Toasts';
import { useSupportWatch, requestNotificationPermission } from '../features/support/useSupportWatch';

const NAV = [
  { to: '/', label: 'Pregled', icon: LayoutDashboard, exact: true },
  { to: '/tenants', label: 'Klijenti', icon: Building2 },
  { to: '/locations', label: 'Lokacije', icon: MapPin },
  { to: '/guest-catalog', label: 'Katalog guest aplikacije', icon: Store },
  { to: '/license-packages', label: 'Licencni paketi', icon: Package },
  { to: '/module-prices', label: 'Cenovnik', icon: Tag },
  { to: '/payments', label: 'Uplate', icon: Wallet },
  { to: '/promo-campaigns', label: 'Promo kampanje', icon: Gift },
  { to: '/affiliate', label: 'Partnerske ponude', icon: Handshake },
  { to: '/units', label: 'Jedinice mere', icon: Calculator },
  { to: '/operators', label: 'Operateri', icon: Users },
  { to: '/accountants', label: 'Knjigovođe', icon: Briefcase },
  { to: '/users', label: 'Admin korisnici', icon: Key },
  { to: '/efaktura', label: 'E-Fakture', icon: FileText },
  { to: '/incoming-receipts', label: 'Ulazni računi', icon: ScanLine },
  { to: '/eotpremnica', label: 'eOtpremnica', icon: Truck },
  { to: '/notifications', label: 'Obaveštenja', icon: Bell },
  { to: '/support', label: 'Prijave problema', icon: LifeBuoy },
  { to: '/assistant', label: 'AI asistent', icon: Bot },
  { to: '/errors', label: 'Greške u radu', icon: Bug },
  { to: '/fiscal-calls', label: 'Pozivi ka PFR-u', icon: ShieldCheck },
  { to: '/lpfr-releases', label: 'L-PFR izdanja', icon: Download },
  { to: '/pos-releases', label: 'Izdanja kase', icon: Download },
  { to: '/audit', label: 'Audit log', icon: Activity },
  { to: '/company', label: 'Podaci firme', icon: Landmark },
  { to: '/legal', label: 'Pravni dokumenti', icon: ScrollText },
  { to: '/system', label: 'Sistem', icon: Database },
];

/**
 * Platformske stavke koje prodavac (`reseller`) ne sme da vidi - backend ih
 * ionako odbija sa 403 (ADR-026), ovo je samo da mu UI ne nudi mrtve linkove.
 * Izvor istine za dozvole je server, ne ova lista.
 */
const RESELLER_HIDDEN = new Set(['/license-packages', '/module-prices', '/payments', '/promo-campaigns', '/affiliate', '/units', '/users', '/audit', '/errors', '/fiscal-calls', '/lpfr-releases', '/pos-releases', '/system', '/accountants', '/assistant',
  // Katalog guest aplikacije je platformski (svi objekti) - server prodavcu ne pušta rutu (fail-closed).
  '/guest-catalog',
  // Podaci NASE firme (racun za uplate) i pravni tekst cele platforme - server ih prodavcu odbija.
  '/company', '/legal']);

/** Ispod ove širine meni je fioka, a ne stalna kolona (telefon, uzak tablet). */
const UZAK_EKRAN = '(max-width: 900px)';

function useUzakEkran(): boolean {
  const [uzak, setUzak] = useState(() => typeof window !== 'undefined' && window.matchMedia(UZAK_EKRAN).matches);
  useEffect(() => {
    const mq = window.matchMedia(UZAK_EKRAN);
    const promena = () => setUzak(mq.matches);
    mq.addEventListener('change', promena);
    return () => mq.removeEventListener('change', promena);
  }, []);
  return uzak;
}

/**
 * Obaveštenja moraju da postoje **iznad** sadržaja koji ih šalje, pa je ljuska
 * podeljena: provajder spolja, a nadzor prijava unutra - tamo gde sme da zove
 * `useToasts`.
 */
export function Shell({ session }: { session: AdminSession }) {
  return (
    <ToastProvider>
      <ShellBody session={session} />
    </ToastProvider>
  );
}

function ShellBody({ session }: { session: AdminSession }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Nadzor stoji u ljusci: prijava stigne najcesce dok covek gleda nesto drugo.
  const { unread, clearUnread } = useSupportWatch();

  // Otvoren spisak prijava je citanje - znacka se gasi.
  useEffect(() => {
    if (location.pathname.startsWith('/support')) clearUnread();
  }, [location.pathname, clearUnread]);

  const [notifyState, setNotifyState] = useState<NotificationPermission>(
    typeof Notification === 'undefined' ? 'denied' : Notification.permission,
  );
  const uzak = useUzakEkran();
  const [meniOtvoren, setMeniOtvoren] = useState(false);

  // Izbor stavke zatvara fioku; na širokom ekranu fioka ne postoji.
  useEffect(() => { setMeniOtvoren(false); }, [location.pathname, uzak]);

  // Otvorena fioka: Esc je zatvara, a stranica ispod se ne pomera.
  useEffect(() => {
    if (!meniOtvoren) return;
    const naEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMeniOtvoren(false); };
    window.addEventListener('keydown', naEsc);
    const ranije = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', naEsc); document.body.style.overflow = ranije; };
  }, [meniOtvoren]);

  const nav = session.admin.role === 'reseller'
    ? NAV.filter((item) => !RESELLER_HIDDEN.has(item.to))
    : NAV;

  return (
    <div style={{ display: 'flex', flexDirection: uzak ? 'column' : 'row', minHeight: '100vh' }}>
      {/* Gornja traka - samo na uskom ekranu, gde je meni sklonjen u fioku. */}
      {uzak && (
        <header style={{
          position: 'sticky', top: 0, zIndex: 40,
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 16px',
          background: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <button
            onClick={() => setMeniOtvoren(true)}
            aria-label="Otvori meni"
            aria-expanded={meniOtvoren}
            data-testid="mobile-menu-open"
            style={{
              position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: 6, border: '1px solid var(--color-border)',
              background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
            }}
          >
            <Menu size={18} />
            {unread > 0 && (
              <span style={{ position: 'absolute', top: -4, right: -4, width: 10, height: 10, borderRadius: 5, background: '#b91c1c' }} />
            )}
          </button>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-strong)' }}>Efikasa <span style={{ fontWeight: 500, color: 'var(--color-text-muted)', fontSize: 12 }}>Admin</span></div>
        </header>
      )}

      {uzak && meniOtvoren && (
        <div
          onClick={() => setMeniOtvoren(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 45, background: 'rgba(0,0,0,0.55)' }}
        />
      )}

      {/* Sidebar */}
      <aside style={{
        width: uzak ? 'min(280px, 85vw)' : 240,
        background: 'var(--color-bg-surface)',
        borderRight: '1px solid var(--color-border)',
        display: 'flex', flexDirection: 'column',
        flexShrink: 0,
        ...(uzak ? {
          position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 50,
          overflowY: 'auto',
          transform: meniOtvoren ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.2s ease',
          visibility: meniOtvoren ? 'visible' : 'hidden',
        } : {}),
      }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>Efikasa</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>Admin Panel</div>
          </div>
          {uzak && (
            <button
              onClick={() => setMeniOtvoren(false)}
              aria-label="Zatvori meni"
              style={{ border: 'none', background: 'transparent', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 }}
            >
              <X size={18} />
            </button>
          )}
        </div>

        <nav style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px',
                  borderRadius: 6,
                  color: isActive ? 'var(--color-text-strong)' : 'var(--color-text-muted)',
                  background: isActive ? 'var(--color-bg-hover)' : 'transparent',
                  textDecoration: 'none',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                })}
              >
                <Icon size={15} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {/* Znacka stoji samo na prijavama i samo dok ima neprocitanog. */}
                {item.to === '/support' && unread > 0 && (
                  <span
                    data-testid="support-badge"
                    style={{
                      minWidth: 18, padding: '1px 6px', borderRadius: 9,
                      background: '#b91c1c', color: '#fff',
                      fontSize: 10, fontWeight: 700, textAlign: 'center',
                    }}
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div style={{ padding: 12, borderTop: '1px solid var(--color-border)' }}>
          <NavLink
            to="/profile"
            style={({ isActive }) => ({
              display: 'block',
              padding: '8px 8px 10px',
              borderRadius: 6,
              fontSize: 11,
              color: 'var(--color-text-muted)',
              textDecoration: 'none',
              background: isActive ? 'var(--color-bg-hover)' : 'transparent',
            })}
          >
            <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>{session.admin.fullName}</div>
            <div>{session.admin.email}</div>
            <div style={{ marginTop: 2, fontSize: 10, opacity: 0.7 }}>Role: {session.admin.role} · profil</div>
          </NavLink>
          {/* Obavestenje pregledaca se vidi i kad je kartica u pozadini, ali
              dozvolu mora da zatrazi KLIK - pregledaci odbijaju zahtev koji
              dolazi sam od sebe pri ucitavanju. */}
          {notifyState === 'default' && (
            <button
              onClick={async () => setNotifyState(await requestNotificationPermission())}
              data-testid="enable-notifications"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '8px 12px', marginBottom: 4,
                borderRadius: 6, border: '1px solid var(--color-border)',
                background: 'transparent', color: 'var(--color-text-muted)',
                cursor: 'pointer', fontSize: 12,
              }}
            >
              <Bell size={14} /> Uključi obaveštenja
            </button>
          )}
          <button
            onClick={() => { logout(); navigate('/'); window.location.reload(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', padding: '8px 12px',
              borderRadius: 6, border: 'none',
              background: 'transparent', color: 'var(--color-text-muted)',
              cursor: 'pointer', fontSize: 13,
            }}
          >
            <LogOut size={14} /> Odjavi se
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main" style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: uzak ? 16 : 32 }}>
        {/* Ručno otkucana platformska adresa: prodavac ide na pregled umesto na praznu stranu. */}
        {session.admin.role === 'reseller' && [...RESELLER_HIDDEN].some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
          ? <Navigate to="/" replace />
          : <Outlet />}
      </main>
    </div>
  );
}
